import React, { useState, useEffect } from 'react';
import { 
  FaGamepad, FaTrophy, FaUsers, FaCog, FaToggleOn, FaToggleOff, 
  FaEdit, FaSave, FaTimes, FaSpinner, FaChartLine, FaCoins, 
  FaExclamationTriangle, FaCheckCircle, FaClock, FaBan 
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formatAmount } from '../../utils/currency';
import axiosInstance from '../../services/axiosConfig';
import './AdminComponents.css';

type P2PGame = {
  id: number;
  name: string;
  type: string;
  minStake: number;
  maxStake: number;
  description: string;
  rules: string;
  avgDuration: string;
  isEnabled: boolean;
  createdAt: string;
};

type GameSession = {
  id: number;
  challengeId: number;
  gameId: number;
  gameName: string | null;
  gameType: string | null;
  player1Id: number;
  player1Username: string | null;
  player1Avatar: string | null;
  player2Id: number;
  player2Username: string | null;
  player2Avatar: string | null;
  gameState: string | null;
  status: string;
  winnerId: number | null;
  winnerUsername: string | null; // Computed field from transformation
  result: string | null;
  multiSigWalletId: number | null;
  platformFee: number | null;
  payoutAmount: number | null;
  totalStake: number | null;
  stake: number; // Computed field mapped from totalStake
  player1Ready: boolean | null;
  player2Ready: boolean | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

type GameStats = {
  totalGames: number;
  totalActiveSessions: number;
  totalCompletedSessions: number;
  totalStaked: number;
  totalPlatformFees: number;
  totalPayouts: number;
  avgSessionDuration: string;
};

const P2PGamesManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'games' | 'sessions' | 'statistics'>('games');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Games state
  const [games, setGames] = useState<P2PGame[]>([]);
  const [editingGame, setEditingGame] = useState<P2PGame | null>(null);
  const [editedGame, setEditedGame] = useState<P2PGame | null>(null);
  const [savingGame, setSavingGame] = useState(false);
  
  // Sessions state
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [sessionFilter, setSessionFilter] = useState<'ALL' | 'WAITING' | 'IN_PROGRESS' | 'COMPLETED'>('ALL');
  
  // Statistics state
  const [statistics, setStatistics] = useState<GameStats | null>(null);

  // Load P2P games
  const loadGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/admin/p2p-games/list');
      
      if (response.data.status === 'success' && response.data.data) {
        setGames(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load games');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      toast.error('Failed to load P2P games');
    } finally {
      setLoading(false);
    }
  };

  // Load game sessions
  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = sessionFilter === 'ALL' 
        ? '/api/admin/p2p-games/sessions'
        : `/api/admin/p2p-games/sessions?status=${sessionFilter}`;
        
      const response = await axiosInstance.get(url);
      
      if (response.data.status === 'success' && response.data.data) {
        // Transform API response to match component expectations
        const transformedSessions = response.data.data.map((session: any) => ({
          ...session,
          // Map totalStake to stake for backward compatibility
          stake: session.totalStake || 0,
          // Derive winnerUsername from winnerId if available
          winnerUsername: session.winnerId 
            ? (session.winnerId === session.player1Id ? session.player1Username : 
               session.winnerId === session.player2Id ? session.player2Username : null)
            : null
        }));
        setSessions(transformedSessions);
      } else {
        throw new Error(response.data.message || 'Failed to load sessions');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      toast.error('Failed to load game sessions');
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get('/api/admin/p2p-games/statistics');
      
      if (response.data.status === 'success' && response.data.data) {
        setStatistics(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load statistics');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  // Toggle game enabled status
  const toggleGameStatus = async (game: P2PGame) => {
    try {
      const response = await axiosInstance.patch(`/api/admin/p2p-games/${game.id}/toggle`, {
        enabled: !game.isEnabled
      });
      
      if (response.data.status === 'success') {
        toast.success(`${game.name} ${!game.isEnabled ? 'enabled' : 'disabled'} successfully`);
        loadGames();
      } else {
        throw new Error(response.data.message || 'Failed to update game status');
      }
    } catch (err: any) {
      toast.error(`Failed to ${!game.isEnabled ? 'enable' : 'disable'} game`);
    }
  };

  // Save game configuration
  const saveGameConfig = async () => {
    if (!editedGame) return;
    
    setSavingGame(true);
    try {
      const response = await axiosInstance.put(`/api/admin/p2p-games/${editedGame.id}`, {
        name: editedGame.name,
        minStake: editedGame.minStake,
        maxStake: editedGame.maxStake,
        description: editedGame.description,
        rules: editedGame.rules,
        avgDuration: editedGame.avgDuration
      });
      
      if (response.data.status === 'success') {
        toast.success('Game configuration updated successfully');
        setEditingGame(null);
        setEditedGame(null);
        loadGames();
      } else {
        throw new Error(response.data.message || 'Failed to update game');
      }
    } catch (err: any) {
      toast.error('Failed to update game configuration');
    } finally {
      setSavingGame(false);
    }
  };

  // Cancel game session (admin intervention)
  const cancelSession = async (sessionId: number) => {
    if (!window.confirm('Are you sure you want to cancel this session? This will refund both players.')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/api/admin/p2p-games/sessions/${sessionId}/cancel`);
      
      if (response.data.status === 'success') {
        toast.success('Session cancelled and players refunded');
        loadSessions();
      } else {
        throw new Error(response.data.message || 'Failed to cancel session');
      }
    } catch (err: any) {
      toast.error('Failed to cancel session');
    }
  };

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'games') {
      loadGames();
    } else if (activeTab === 'sessions') {
      loadSessions();
    } else if (activeTab === 'statistics') {
      loadStatistics();
    }
  }, [activeTab, sessionFilter]);

  // Render game status badge
  const renderStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      WAITING: { color: '#fbb445', icon: <FaClock />, label: 'Waiting' },
      IN_PROGRESS: { color: '#086afb', icon: <FaGamepad />, label: 'In Progress' },
      COMPLETED: { color: '#0dca9d', icon: <FaCheckCircle />, label: 'Completed' },
      CANCELLED: { color: '#ff7171', icon: <FaBan />, label: 'Cancelled' },
      DISPUTED: { color: '#e7038e', icon: <FaExclamationTriangle />, label: 'Disputed' }
    };
    
    const config = statusConfig[status] || statusConfig.WAITING;
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: `${config.color}15`,
        color: config.color,
        borderRadius: 16,
        fontSize: 12,
        fontWeight: 600
      }}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
          <FaGamepad style={{ color: '#086afb' }} />
          P2P Games Management
        </h1>
        <p style={{ color: '#666', fontSize: 14 }}>
          Manage peer-to-peer game betting configuration, sessions, and statistics
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, borderBottom: '2px solid #f0f0f0' }}>
        <button
          onClick={() => setActiveTab('games')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'games' ? '3px solid #086afb' : '3px solid transparent',
            color: activeTab === 'games' ? '#086afb' : '#666',
            fontWeight: activeTab === 'games' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: 14
          }}
        >
          <FaCog style={{ marginRight: 8 }} />
          Game Configuration
        </button>
        <button
          onClick={() => setActiveTab('sessions')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'sessions' ? '3px solid #086afb' : '3px solid transparent',
            color: activeTab === 'sessions' ? '#086afb' : '#666',
            fontWeight: activeTab === 'sessions' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: 14
          }}
        >
          <FaUsers style={{ marginRight: 8 }} />
          Game Sessions
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          style={{
            padding: '12px 24px',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'statistics' ? '3px solid #086afb' : '3px solid transparent',
            color: activeTab === 'statistics' ? '#086afb' : '#666',
            fontWeight: activeTab === 'statistics' ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: 14
          }}
        >
          <FaChartLine style={{ marginRight: 8 }} />
          Statistics
        </button>
      </div>

      {/* Loading & Error States */}
      {loading && !games.length && !sessions.length && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <FaSpinner className="fa-spin" style={{ fontSize: 32, color: '#086afb', marginBottom: 16 }} />
          <p style={{ color: '#666' }}>Loading...</p>
        </div>
      )}

      {error && (
        <div style={{ 
          padding: 16, 
          background: '#fff3f3', 
          border: '1px solid #ffcccc', 
          borderRadius: 8, 
          color: '#cc0000',
          marginBottom: 24 
        }}>
          {error}
        </div>
      )}

      {/* Games Tab */}
      {activeTab === 'games' && !loading && (
        <div>
          <div style={{ display: 'grid', gap: 16 }}>
            {games.map((game) => (
              <div
                key={game.id}
                style={{
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: 12,
                  padding: 20,
                  transition: 'all 0.2s',
                  boxShadow: editingGame?.id === game.id ? '0 4px 12px rgba(8, 106, 251, 0.15)' : 'none'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1a1a1a', margin: 0 }}>
                        {editingGame?.id === game.id ? (
                          <input
                            type="text"
                            value={editedGame?.name || ''}
                            onChange={(e) => setEditedGame({ ...editedGame!, name: e.target.value })}
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 6,
                              padding: 8,
                              fontSize: 16,
                              width: 300
                            }}
                          />
                        ) : (
                          game.name
                        )}
                      </h3>
                      <span style={{
                        padding: '4px 10px',
                        background: game.isEnabled ? '#d4f4dd' : '#ffe6e6',
                        color: game.isEnabled ? '#0dca9d' : '#ff7171',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {game.isEnabled ? 'Active' : 'Disabled'}
                      </span>
                      <span style={{
                        padding: '4px 10px',
                        background: '#f0f8ff',
                        color: '#086afb',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600
                      }}>
                        {game.type}
                      </span>
                    </div>

                    {editingGame?.id === game.id ? (
                      <div style={{ display: 'grid', gap: 12, marginTop: 16 }}>
                        <div>
                          <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Min Stake (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editedGame?.minStake || 0}
                            onChange={(e) => setEditedGame({ ...editedGame!, minStake: parseFloat(e.target.value) })}
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 6,
                              padding: 8,
                              fontSize: 14,
                              width: 150
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Max Stake (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editedGame?.maxStake || 0}
                            onChange={(e) => setEditedGame({ ...editedGame!, maxStake: parseFloat(e.target.value) })}
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 6,
                              padding: 8,
                              fontSize: 14,
                              width: 150
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Average Duration</label>
                          <input
                            type="text"
                            value={editedGame?.avgDuration || ''}
                            onChange={(e) => setEditedGame({ ...editedGame!, avgDuration: e.target.value })}
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 6,
                              padding: 8,
                              fontSize: 14,
                              width: 200
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Description</label>
                          <textarea
                            value={editedGame?.description || ''}
                            onChange={(e) => setEditedGame({ ...editedGame!, description: e.target.value })}
                            rows={2}
                            style={{
                              border: '1px solid #ccc',
                              borderRadius: 6,
                              padding: 8,
                              fontSize: 14,
                              width: '100%',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>{game.description}</p>
                        <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
                          <div>
                            <span style={{ color: '#999' }}>Stake Range: </span>
                            <span style={{ fontWeight: 600, color: '#1a1a1a' }}>
                              {formatAmount(game.minStake, 'EUR')} - {formatAmount(game.maxStake, 'EUR')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#999' }}>Avg Duration: </span>
                            <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{game.avgDuration}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    {editingGame?.id === game.id ? (
                      <>
                        <button
                          onClick={saveGameConfig}
                          disabled={savingGame}
                          style={{
                            padding: '8px 16px',
                            background: '#0dca9d',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: savingGame ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          {savingGame ? <FaSpinner className="fa-spin" /> : <FaSave />}
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingGame(null);
                            setEditedGame(null);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#f0f0f0',
                            color: '#666',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          <FaTimes />
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingGame(game);
                            setEditedGame({ ...game });
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#f0f8ff',
                            color: '#086afb',
                            border: '1px solid #086afb',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => toggleGameStatus(game)}
                          style={{
                            padding: '8px 16px',
                            background: game.isEnabled ? '#fff3f3' : '#d4f4dd',
                            color: game.isEnabled ? '#ff7171' : '#0dca9d',
                            border: `1px solid ${game.isEnabled ? '#ff7171' : '#0dca9d'}`,
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          {game.isEnabled ? <FaToggleOff /> : <FaToggleOn />}
                          {game.isEnabled ? 'Disable' : 'Enable'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {activeTab === 'sessions' && !loading && (
        <div>
          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {(['ALL', 'WAITING', 'IN_PROGRESS', 'COMPLETED'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setSessionFilter(filter)}
                style={{
                  padding: '8px 16px',
                  background: sessionFilter === filter ? '#086afb' : '#f0f0f0',
                  color: sessionFilter === filter ? '#fff' : '#666',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {filter.replace('_', ' ')}
              </button>
            ))}
          </div>

          {/* Sessions Table */}
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f8f8' }}>
                <tr>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>ID</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Game</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Player 1</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Player 2</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Stake</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Winner</th>
                  <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Created</th>
                  <th style={{ padding: 12, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#666', borderBottom: '1px solid #e0e0e0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session, idx) => (
                  <tr key={session.id} style={{ borderBottom: idx < sessions.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <td style={{ padding: 12, fontSize: 13 }}>#{session.id}</td>
                    <td style={{ padding: 12, fontSize: 13, fontWeight: 600 }}>
                      {session.gameName || session.gameType || `Game #${session.gameId}`}
                    </td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {session.player1Username || `Player #${session.player1Id}`}
                    </td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {session.player2Username || `Player #${session.player2Id}`}
                    </td>
                    <td style={{ padding: 12, fontSize: 13, fontWeight: 600 }}>
                      {session.stake > 0 ? formatAmount(session.stake, 'EUR') : '-'}
                    </td>
                    <td style={{ padding: 12 }}>{renderStatusBadge(session.status)}</td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {session.winnerUsername ? (
                        <span style={{ color: '#0dca9d', fontWeight: 600 }}>{session.winnerUsername}</span>
                      ) : session.result === 'DRAW' ? (
                        <span style={{ color: '#fbb445' }}>Draw</span>
                      ) : session.winnerId ? (
                        <span style={{ color: '#0dca9d', fontWeight: 600 }}>Player #{session.winnerId}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{ padding: 12, fontSize: 13, color: '#666' }}>
                      {session.createdAt ? new Date(session.createdAt).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {(session.status === 'WAITING' || session.status === 'IN_PROGRESS') && (
                        <button
                          onClick={() => cancelSession(session.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#fff3f3',
                            color: '#ff7171',
                            border: '1px solid #ff7171',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          <FaBan style={{ marginRight: 4 }} />
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sessions.length === 0 && (
              <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
                No sessions found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Statistics Tab */}
      {activeTab === 'statistics' && !loading && statistics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#f0f8ff', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaGamepad style={{ fontSize: 20, color: '#086afb' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Total Games</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{statistics.totalGames}</h2>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#fff3e6', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaClock style={{ fontSize: 20, color: '#fbb445' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Active Sessions</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{statistics.totalActiveSessions}</h2>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#d4f4dd', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaCheckCircle style={{ fontSize: 20, color: '#0dca9d' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Completed Sessions</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{statistics.totalCompletedSessions}</h2>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#f3e6ff', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaCoins style={{ fontSize: 20, color: '#e7038e' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Total Staked</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{formatAmount(statistics.totalStaked, 'EUR')}</h2>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#d4f4dd', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaTrophy style={{ fontSize: 20, color: '#0dca9d' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Total Payouts</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{formatAmount(statistics.totalPayouts, 'EUR')}</h2>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ 
                width: 48, 
                height: 48, 
                background: '#fff8e6', 
                borderRadius: 12, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FaChartLine style={{ fontSize: 20, color: '#fbb445' }} />
              </div>
              <div>
                <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Platform Fees</p>
                <h2 style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{formatAmount(statistics.totalPlatformFees, 'EUR')}</h2>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default P2PGamesManagement;

